/*
 * Copyright (c) 2023 Bluemate B.V.
 */
import './style/dvrdDatePicker.scss';
import {AwesomeIcon, ChangeFunction, ErrorType} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import classNames from 'classnames';
import React, {ReactNode, useState} from 'react';

import DatePickerPopup from './datePickerPopup';

interface Props {
    onChange: ChangeFunction<IDate>;
    closeOnChange?: boolean;
    value: IDate | null;
    label: ReactNode;
    error?: ErrorType;
    className?: string;
    placeholder?: string;
    dateFormat?: string;
    disabled?: boolean;
    min?: IDate | null;
    max?: IDate | null;
    allowBodyScroll?: boolean;
}

export default function DvrdDatePicker(props: Props) {
    const {
        onChange,
        className,
        closeOnChange,
        error,
        label,
        value,
        dateFormat,
        placeholder,
        disabled,
        max,
        min,
        allowBodyScroll
    } = props;
    const [pickerOpen, setPickerOpen] = useState(false);

    function onClickContainer() {
        if (!disabled) setPickerOpen(true);
    }

    const onChangePicker = (daySelected: boolean) => (value: IDate) => {
        if (min && value.isBefore(min, 'day')) return;
        if (max && value.isAfter(max, 'day')) return;
        onChange(value.clone());
        if (closeOnChange && daySelected) onClosePicker();
    };

    function onClosePicker() {
        setPickerOpen(false);
    }

    const format = dateFormat ?? 'YYYY-MM-DD';

    return (
        <div className={classNames('dvrd-date-picker', className, error && 'error', disabled && 'disabled')}>
            <label className='field-label'>{label}</label>
            <div className='value-container' onClick={onClickContainer}>
                <label className={classNames('value', !value && 'placeholder')}>
                    {value?.format(format) ?? placeholder ?? ''}
                </label>
                <AwesomeIcon name='calendar-alt' className='calendar-icon' />
            </div>
            <DatePickerPopup
                onClose={onClosePicker}
                onChange={onChangePicker}
                open={pickerOpen}
                value={value}
                max={max}
                min={min}
                allowBodyScroll={allowBodyScroll}
            />
            <label className='error-label'>{error}</label>
        </div>
    );
}
